import React, { useEffect, useState, useRef, useCallback } from 'react';
import GameCard from "../gamecard/gameCard";
import AdSideBar from "../ad/adSideBar";
import Loader from "../Loader/loader";
import LineSeparator from "../bgMadness/line";
import CardGrid from "../gamecard/cardGrid";
import AllGames from "../../data/gameData.json";
import AllCategories from "../../data/category.json";
import slugify from "../../utils/slugify";
import { Helmet } from 'react-helmet-async';

import Fuse from "fuse.js";
import { Button } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';

const GAMES_PER_PAGE = 24;

const CategoryList = () => {
  const [allGames, setAllGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filteredGames, setFilteredGames] = useState([]);
  const [category, selectedCategory] = useState("");
  const observerRef = useRef();
  const location = useLocation();

  const fuse = new Fuse(AllGames.data, {
    keys: ["title"],
    threshold: 0.3,
  });
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    let filtered = AllGames.data.filter(g => g.category === category);
    if (filtered.length == 0 || category == "") {
      let prefix = category.substring(0, 3);
      if (!prefix) {
        prefix = "data"
      }
      const matchedKey = Object.keys(AllGames).find(key => key.startsWith(prefix));
      if (matchedKey && AllGames[matchedKey]) {
        filtered = AllGames[matchedKey]
      } else {
        filtered = AllGames.data;
      }
    }

    setFilteredGames(filtered);
    setPage(1);
    setAllGames(filtered.slice(0, GAMES_PER_PAGE));
    setHasMore(filtered.length > GAMES_PER_PAGE);
    setLoading(false);
  }, [category]);


  const loadMoreGames = () => {
    const startIndex = page * GAMES_PER_PAGE;
    const newItems = filteredGames.slice(startIndex, startIndex + GAMES_PER_PAGE);
    setAllGames(prev => [...prev, ...newItems]);
    setHasMore(startIndex + GAMES_PER_PAGE < filteredGames.length);
    setPage(prev => prev + 1);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    const queryParams = new URLSearchParams(location.search);
    const initialTerm = queryParams.get("c") || "";
    selectedCategory(initialTerm);
  }, [location.search]);

  const lastElementRef = useCallback((node) => {
    if (loading || !hasMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadMoreGames();
      }
    });
    if (node) observerRef.current.observe(node);
  }, [loading, hasMore, filteredGames, page]);

  return (
    <div className="relative min-h-screen bg-[#0d0d1a] text-[#00F0FF] px-4 sm:px-6 lg:px-10 py-8 overflow-hidden">
      <Helmet>
        <title>Categories | BrowserArena</title>
        <meta name="description" content="Search and filter hundreds of free browser games by category, tags, or titles." />
        <link rel="canonical" href="https://browserarena.com/categories" />
      </Helmet>

      {/* Background Lights */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a3e] via-[#12122b] to-[#0d0d1a] opacity-90 -z-10" />
      <div className="absolute top-10 left-10 w-72 h-72 bg-[#00F0FF] opacity-20 rounded-full blur-[100px] -z-10 animate-pulse" />
      <div className="absolute bottom-10 right-10 w-60 h-60 bg-[#FF007F] opacity-20 rounded-full blur-[80px] -z-10 animate-pulse" />

      {/* Category Filter */}
      <section className="mt-5 px-6 py-10 max-w-5xl mx-auto rounded-2xl border border-[#00F0FF] bg-[#111122]/80 shadow-lg shadow-[#00F0FF]/20 backdrop-blur-lg text-[#CCF6FF]">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-1 max-w-5xl mx-auto">
          {AllCategories.map((cat, index) => (
            <Button
              key={index}
              onClick={() => selectedCategory(cat.title)}
              className={`text-sm text-center px-3 py-2 rounded border ${category === cat.title
                ? "bg-[#FF007F] text-white border-[#FF007F]"
                : "bg-[#1a1a2e] border-[#00F0FF] text-[#00F0FF] hover:border-[#FF007F]"
                } transition`}
            >
              {cat.title}
            </Button>
          ))}
        </div>
      </section>

      {/* Grid Section */}
      <LineSeparator/>
      <CardGrid allGames={allGames} />


      {/* Loader */}
      {hasMore && (
        <div ref={lastElementRef} className="flex justify-center mt-10">
          <Loader />
        </div>
      )}
    </div>
  );
};

export default CategoryList;
